import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Empty, Popconfirm, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { useChildOrganizations, useDeleteOrganization } from "../hooks/useOrganizations";
import { useOrganizationsStore } from "../store/organizations.store";
import type { Organization } from "../types";

const { Text } = Typography;

interface OrgChildrenTabProps {
	organizationId: string;
}

const LIMIT = 10;

function renderStatus(record: Organization) {
	if (record.isLock) {
		return <Tag color="red">Bloklangan</Tag>;
	}
	if (record.isEducational) {
		return <Tag color="green">Ta'lim</Tag>;
	}
	return <Tag>Faol</Tag>;
}

export function OrgChildrenTab({ organizationId }: OrgChildrenTabProps) {
	const { page, setPage } = useUrlTableState({ prefix: "children" });
	const navigate = useNavigate();
	const { mutate: remove } = useDeleteOrganization();
	const openEditModal = useOrganizationsStore((s) => s.openEditModal);

	const { data, isLoading } = useChildOrganizations(organizationId, { page, limit: LIMIT });

	const columns: ColumnsType<Organization> = [
		{
			title: "№",
			key: "index",
			width: 55,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => (page - 1) * LIMIT + index + 1,
		},
		{
			title: "Nomi",
			dataIndex: "name",
			key: "name",
			render: (name: string, record: Organization) => (
				<Button
					type="link"
					className="p-0 h-auto font-medium text-left"
					onClick={() => navigate(`/organizations/${record.id}`)}
				>
					{name}
				</Button>
			),
		},
		{
			title: "Turi",
			key: "type",
			width: 160,
			render: (_: unknown, record: Organization) =>
				record.type ? <Tag color="blue">{record.type.name}</Tag> : <Tag>—</Tag>,
		},
		{
			title: "Hudud",
			key: "soato",
			width: 180,
			render: (_: unknown, record: Organization) =>
				record.soato?.nameUz ?? <Text type="secondary">—</Text>,
		},
		{
			title: "Telefon",
			dataIndex: "phoneNumber",
			key: "phoneNumber",
			width: 150,
		},
		{
			title: "Holat",
			key: "status",
			width: 110,
			align: "center",
			render: (_: unknown, record: Organization) => renderStatus(record),
		},
		{
			title: "Amallar",
			key: "actions",
			width: 80,
			align: "center",
			render: (_: unknown, record: Organization) => (
				<Dropdown
					menu={{
						items: [
							{
								key: "edit",
								icon: <EditOutlined />,
								label: "Tahrirlash",
								onClick: () => openEditModal(record),
							},
							{
								key: "delete",
								danger: true,
								label: (
									// biome-ignore lint/a11y/useKeyWithClickEvents: ignore
									// biome-ignore lint/a11y/noStaticElementInteractions: ignore
									<div onClick={(e) => e.stopPropagation()}>
										<Popconfirm
											title="O'chirish"
											description="Rostdan ham o'chirmoqchimisiz?"
											onConfirm={() => remove(record.id)}
											okText="Ha"
											cancelText="Yo'q"
											okButtonProps={{ danger: true }}
										>
											<div
												style={{ width: "100%", display: "flex", alignItems: "center", gap: "8px" }}
											>
												<DeleteOutlined />
												<span>O'chirish</span>
											</div>
										</Popconfirm>
									</div>
								),
							},
						],
					}}
					trigger={["click"]}
					placement="bottomRight"
				>
					<Button type="text" icon={<MoreOutlined />} size="small" />
				</Dropdown>
			),
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={data?.data?.items}
			loading={isLoading}
			rowKey="id"
			pagination={{
				current: page,
				pageSize: LIMIT,
				total: data?.data?.meta?.total,
				onChange: setPage,
				showSizeChanger: false,
				showTotal: (total) => `Jami: ${total} ta tashkilot`,
			}}
			size="middle"
			locale={{
				emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ma'lumot yo'q" />,
			}}
		/>
	);
}
